import CheckboxCell from "@app/components/Table/CheckboxCell";
import CheckboxHeader from "@app/components/Table/Header/Checkbox";
import { useSetToast } from "@app/components/Toast";
import { useNavigation } from "@app/lib/navigation";
import { SubthemeTag } from "@app/shared/components/Theme/SubthemeTag";
import { ThemeTag } from "@app/shared/components/Theme/ThemeTag";
import UserChipSelect from "@app/shared/components/UserChipSelect";
import { UserTag } from "@app/shared/components/UserTag";
import { useHandleMutationToasts } from "@app/shared/utils/displayMutationToasts";
import { compareUser } from "@app/shared/utils/tableSortingFns";
import { currentUserAtom } from "@app/store/userStore";
import { getTargetToDisplay } from "@app/usecases/ObjectivesUseCases";
import { normalizeString } from "@app/utils/string";
import { Icon } from "@design-system/Icon";
import { FlexRow } from "@design-system/Layout/Flex";
import { type ColumnDef, compareBasic } from "@design-system/Table";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { Text } from "@design-system/Typography/Text";
import { type ObjectiveStatus, UserRole } from "@generated/client/graphql";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateObjective } from "../../data";
import { StatusTagSelect } from "../../objective/components/ObjectiveDefinition/StatusTagSelect";
import { RESOURCE_NAME, keys } from "./i18n";
import { type Objective } from "./types";

const selectCol: ColumnDef<Objective> = {
    id: "select",
    header: ({ table }) => {
        return <CheckboxHeader table={table} />;
    },
    cell: ({ row }) => <CheckboxCell row={row} />,
    meta: {
        colWidth: "50px",
        headerClassName: "min-w-[50px] max-w-[50px] border-r-0 gap-3",
        cellClassName:
            "min-w-[50px] max-w-[50px] group-hover/tablerow:bg-secondary border-r-0 gap-3",
    },
};

const nameCol: ColumnDef<Objective> = {
    id: "name",
    accessorKey: "name",
    meta: {
        colWidth: "400px",
        headerClassName: "max-w-[400px]",
        cellClassName: "max-w-[400px]",
    },
    header: () => {
        const { t } = useTranslation(RESOURCE_NAME);
        return (
            <Text variant="body-sm-bold" color="secondary">
                {t(keys.header_name)}
            </Text>
        );
    },
    cell: ({ row }) => {
        const { t } = useTranslation(RESOURCE_NAME);

        const { navigateFromHome } = useNavigation();
        const handleNameClick = () => {
            navigateFromHome(`objectives/${row.original.id}`);
        };

        const [status, setStatus] = useState<ObjectiveStatus>(
            row.original.status,
        );
        const { updateStatus } = useUpdateStatus(
            row.original.id,
            row.original.entityId,
        );

        useEffect(() => {
            setStatus(row.original.status);
        }, [row.original.status]);

        const handleStatusChange = (newStatus: ObjectiveStatus) => {
            setStatus(newStatus);
            updateStatus(status, newStatus);
        };

        return (
            <FlexRow
                alignItems="center"
                w="full"
                gap="3"
                className="cursor-pointer hover:underline"
                onClick={handleNameClick}
            >
                <StatusTagSelect
                    status={status}
                    setStatus={handleStatusChange}
                    displayStatusName={false}
                    className="shadow-none border-none"
                />
                <Ellipsis asChild hasTooltip lineClamp={2}>
                    <Text
                        variant="body"
                        color={row.original.name ? "primary" : "secondary"}
                    >
                        {row.original.name || t(keys.default_title_placeholder)}
                    </Text>
                </Ellipsis>
            </FlexRow>
        );
    },
};

const themeCol: ColumnDef<Objective> = {
    id: "theme",
    accessorKey: "themeId",
    filterFn: "arrIncludesSome",
    sortingFn: (rowA, rowB) => {
        const themeA = rowA.original.theme?.name ?? "";
        const themeB = rowB.original.theme?.name ?? "";

        return compareBasic(normalizeString(themeA), normalizeString(themeB));
    },
    meta: {
        colWidth: "200px",
        headerClassName: "max-w-[200px]",
        cellClassName: "max-w-[200px]",
    },
    header: () => {
        const { t } = useTranslation(RESOURCE_NAME);

        return (
            <Text variant="body-sm-bold" color="secondary">
                {t(keys.header_theme)}
            </Text>
        );
    },
    cell: ({
        row: {
            original: { themeId },
        },
    }) => <ThemeTag themeId={themeId} />,
};

const subthemeCol: ColumnDef<Objective> = {
    id: "subtheme",
    accessorKey: "subthemeId",
    filterFn: "arrIncludesSome",
    sortingFn: (rowA, rowB) => {
        const subthemeA = rowA.original.subtheme?.name ?? "";
        const subthemeB = rowB.original.subtheme?.name ?? "";

        return compareBasic(
            normalizeString(subthemeA),
            normalizeString(subthemeB),
        );
    },
    meta: {
        colWidth: "200px",
        headerClassName: "max-w-[200px]",
        cellClassName: "max-w-[200px]",
    },
    header: () => {
        const { t } = useTranslation(RESOURCE_NAME);

        return (
            <Text variant="body-sm-bold" color="secondary">
                {t(keys.header_subtheme)}
            </Text>
        );
    },
    cell: ({ row }) => {
        const themeId = row.original.themeId;
        const subThemeId = row.original.subthemeId;

        return (
            <SubthemeTag
                themeId={themeId}
                subthemeId={subThemeId ?? undefined}
                isTrigger={false}
            />
        );
    },
};

const ownerCol: ColumnDef<Objective> = {
    id: "owner",
    accessorKey: "owner.id",
    meta: {
        colWidth: "200px",
        headerClassName: "max-w-[200px]",
        cellClassName: "max-w-[200px]",
    },
    filterFn: (row, _, filterValue) => {
        return (
            filterValue.filter((elt: string) => {
                if (elt === null) {
                    return !row.original.owner;
                } else {
                    return row.original.owner?.id === elt;
                }
            }).length > 0
        );
    },
    sortingFn: (rowA, rowB) => {
        const ownerA = rowA.original.owner;
        const ownerB = rowB.original.owner;

        return compareUser(ownerA, ownerB);
    },

    header: () => {
        const { t } = useTranslation(RESOURCE_NAME);
        return (
            <Text variant="body-sm-bold" color="secondary">
                {t(keys.header_owner)}
            </Text>
        );
    },
    cell: ({ row }) => {
        const { t } = useTranslation(RESOURCE_NAME);
        const [ownerId, setOwnerId] = useState<string | undefined>(
            row.original.owner?.id,
        );
        const [searchFilter, setSearchFilter] = useState<string>();
        const { updateOwner } = useUpdateOwner(
            row.original.id,
            row.original.entityId,
        );

        useEffect(() => {
            setOwnerId(row.original.owner?.id);
        }, [row.original.owner?.id]);

        const handleUserIdChange = (userId: string) => {
            setOwnerId(userId);
            updateOwner(ownerId, userId);
        };

        const { data: currentUser } = useAtomValue(currentUserAtom);
        const isContributor =
            currentUser?.currentUser.permission?.role === UserRole.Contributor;
        if (isContributor) {
            return (
                <Text variant="body" color="primary">
                    {currentUser?.currentUser.firstName}{" "}
                    {currentUser?.currentUser.lastName}
                </Text>
            );
        }

        return (
            <UserChipSelect
                userId={ownerId}
                setUserId={handleUserIdChange}
                searchFilter={searchFilter}
                onSearchFilterChange={setSearchFilter}
                trigger={
                    <UserTag
                        userId={ownerId || undefined}
                        noUserText={t(keys.no_owner_placeholder)}
                        isTrigger
                    />
                }
            />
        );
    },
};

const targetCol: ColumnDef<Objective> = {
    id: "target",
    meta: {
        colWidth: "125px",
        headerClassName: "max-w-[125px]",
        cellClassName: "max-w-[125px]",
    },
    header: () => {
        const { t } = useTranslation(RESOURCE_NAME);
        return (
            <FlexRow alignItems="center">
                <Text variant="body-sm-bold" color="secondary">
                    {t(keys.header_target)}
                </Text>
            </FlexRow>
        );
    },
    cell: ({ row }) => {
        const targetToDisplay = getTargetToDisplay(row.original.targets);

        const textToDisplay =
            targetToDisplay?.value?.number !== undefined
                ? `${targetToDisplay.value.number} ${row.original.indicator?.unit?.shortName || ""}`
                : "-";

        return (
            <FlexRow gap="2" alignItems="center">
                <Text
                    variant="body"
                    color={targetToDisplay?.value ? "primary" : "secondary"}
                >
                    {textToDisplay}
                </Text>
                {targetToDisplay?.value && (
                    <Icon name="flag" size="sm" className="text-secondary" />
                )}
            </FlexRow>
        );
    },
};

const dateCol: ColumnDef<Objective> = {
    id: "date",
    accessorFn: (row) => {
        const targetToDisplay = getTargetToDisplay(row.targets);
        return targetToDisplay?.date;
    },
    sortDescFirst: false,
    meta: {
        colWidth: "125px",
        headerClassName: "max-w-[125px]",
        cellClassName: "max-w-[125px]",
    },
    header: () => {
        const { t } = useTranslation(RESOURCE_NAME);
        return (
            <FlexRow alignItems="center">
                <Text variant="body-sm-bold" color="secondary">
                    {t(keys.header_date)}
                </Text>
            </FlexRow>
        );
    },
    cell: ({ row }) => {
        const targetToDisplay = getTargetToDisplay(row.original.targets);

        dayjs.extend(LocalizedFormat);

        const displayDate = targetToDisplay?.date
            ? dayjs(targetToDisplay.date).format("MMM YYYY")
            : "-";

        return (
            <FlexRow gap="2" alignItems="center">
                <Ellipsis asChild hasTooltip>
                    <Text>{displayDate}</Text>
                </Ellipsis>
                {targetToDisplay?.date && (
                    <Icon
                        name="calendar"
                        size="sm"
                        className="text-secondary"
                    />
                )}
            </FlexRow>
        );
    },
};

const useUpdateOwner = (objectiveId: string, entityId: string) => {
    const { mutate, isPending } = useUpdateObjective(objectiveId, entityId);
    const { t } = useTranslation(RESOURCE_NAME);
    const { setToastError } = useSetToast();
    const handleMutationToasts = useHandleMutationToasts();

    const updateOwner = (currentOwnerId?: string, newUserId?: string) => {
        const newOwnerId = newUserId ? newUserId : null;
        if (currentOwnerId === newOwnerId) return;

        mutate(
            { id: objectiveId, ownerId: newOwnerId },
            {
                onSettled: (data) => {
                    handleMutationToasts(data?.updateObjective, {
                        successMessage: t(keys.update_success),
                        defaultErrorMessage: t(keys.update_error),
                    });
                },
                onError: () => {
                    setToastError(t(keys.update_error));
                },
            },
        );
    };

    return { updateOwner, isPending };
};

const useUpdateStatus = (objectiveId: string, entityId: string) => {
    const { mutate, isPending } = useUpdateObjective(objectiveId, entityId);
    const { t } = useTranslation(RESOURCE_NAME);
    const { setToastError } = useSetToast();
    const handleMutationToasts = useHandleMutationToasts();

    const updateStatus = (
        currentStatus: ObjectiveStatus,
        newStatus: ObjectiveStatus,
    ) => {
        if (currentStatus === newStatus) return;

        mutate(
            { id: objectiveId, status: newStatus },
            {
                onSettled: (data) => {
                    handleMutationToasts(data?.updateObjective, {
                        successMessage: t(keys.update_success),
                        defaultErrorMessage: t(keys.update_error),
                    });
                },
                onError: () => {
                    setToastError(t(keys.update_error));
                },
            },
        );
    };

    return { updateStatus, isPending };
};

export const useColumns = (): ColumnDef<Objective>[] => {
    const columns = [
        selectCol,
        nameCol,
        themeCol,
        subthemeCol,
        ownerCol,
        dateCol,
        targetCol,
    ];
    return columns;
};
